import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, Edit2Icon, CheckIcon, XIcon, MoreVertical, Edit, CalendarIcon, UsersIcon, CopyIcon } from 'lucide-react';

/**
 * BandLineupManager component for managing band lineups
 * Uses card-based layout consistent with setlists
 */
const BandLineupManager = ({ band, onUpdate }) => {
  const navigate = useNavigate();
  const [showNewLineupForm, setShowNewLineupForm] = useState(false);
  const [lineupNameInput, setLineupNameInput] = useState('');
  const [editingLineup, setEditingLineup] = useState(null);
  const [localMembers, setLocalMembers] = useState([]);
  const [editingLineupName, setEditingLineupName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const lineups = band?.lineups || [];

  // Format date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Add a new lineup
  const handleAddLineup = async () => {
    if (!lineupNameInput.trim()) return;

    try {
      const response = await fetch(`/api/bands/${band._id}/lineups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: lineupNameInput.trim(),
          description: '',
          members: []
        })
      });

      if (response.ok) {
        if (onUpdate) onUpdate();
        setShowNewLineupForm(false);
        setLineupNameInput('');
      } else {
        const error = await response.json();
        alert(`Failed to add lineup: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding lineup:', error);
      alert('Failed to add lineup');
    }
  };

  // Delete lineup
  const handleDeleteLineup = async (lineupId) => {
    if (!window.confirm('Are you sure you want to delete this lineup?')) return;

    try {
      const response = await fetch(`/api/bands/${band._id}/lineups/${lineupId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        alert(`Failed to delete lineup: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting lineup:', error);
      alert('Failed to delete lineup');
    }
  };

  // Duplicate lineup
  const handleDuplicateLineup = async (lineup) => {
    try {
      // Generate a unique name for the duplicate
      const copyName = `${lineup.name} (Copy)`;
      
      const response = await fetch(`/api/bands/${band._id}/lineups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: copyName,
          description: lineup.description || '',
          members: JSON.parse(JSON.stringify(lineup.members || [])) // Deep copy the members array
        })
      });

      if (response.ok) {
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        alert(`Failed to duplicate lineup: ${error.error}`);
      }
    } catch (error) {
      console.error('Error duplicating lineup:', error);
      alert('Failed to duplicate lineup');
    }
  };

  // Open lineup for editing
  const handleEditLineup = (lineup) => {
    setEditingLineup(lineup);
    setLocalMembers(JSON.parse(JSON.stringify(lineup.members || [])));
    setEditingLineupName(lineup.name || '');
    setHasChanges(false);
  };

  // Close editing modal
  const handleCloseEdit = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to close?')) {
      return;
    }
    setEditingLineup(null);
    setLocalMembers([]);
    setEditingLineupName('');
    setHasChanges(false);
  };

  // Add member (local state)
  const handleAddMember = () => {
    setLocalMembers([...localMembers, { name: '', role: '' }]);
    setHasChanges(true);
  };

  // Update member (local state)
  const handleUpdateMember = (memberIndex, field, value) => {
    const updatedMembers = [...localMembers];
    updatedMembers[memberIndex][field] = value;
    setLocalMembers(updatedMembers);
    setHasChanges(true);
  };

  // Delete member (local state)
  const handleDeleteMember = (memberIndex) => {
    const updatedMembers = localMembers.filter((_, i) => i !== memberIndex);
    setLocalMembers(updatedMembers);
    setHasChanges(true);
  };

  // Move member (local state)
  const handleMoveMember = (memberIndex, direction) => {
    const newIndex = direction === 'up' ? memberIndex - 1 : memberIndex + 1;
    if (newIndex < 0 || newIndex >= localMembers.length) return;

    const updatedMembers = [...localMembers];
    const [removed] = updatedMembers.splice(memberIndex, 1);
    updatedMembers.splice(newIndex, 0, removed);
    
    setLocalMembers(updatedMembers);
    setHasChanges(true);
  };

  // Update lineup name (local state)
  const handleUpdateLineupName = (name) => {
    setEditingLineupName(name);
    setHasChanges(true);
  };

  // Save changes
  const handleSave = async () => {
    if (!editingLineup) return;

    // Validate lineup name
    if (!editingLineupName.trim()) {
      alert('Lineup name cannot be empty');
      return;
    }

    // Validate members
    for (const member of localMembers) {
      if (!member.name.trim() || !member.role.trim()) {
        alert('All members must have both name and role filled in');
        return;
      }
    }

    try {
      const response = await fetch(`/api/bands/${band._id}/lineups/${editingLineup._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: editingLineupName.trim(),
          members: localMembers
        })
      });

      if (response.ok) {
        if (onUpdate) onUpdate();
        setHasChanges(false);
        setEditingLineup(null);
        setLocalMembers([]);
      } else {
        const error = await response.json();
        alert(`Failed to save lineup: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving lineup:', error);
      alert('Failed to save lineup');
    }
  };

  // Lineup Card Component
  const LineupCard = ({ lineup }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!showDropdown) return;
      
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setShowDropdown(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    return (
      <div 
        className="border border-border rounded-md p-4 bg-card hover:bg-card/80 transition-colors relative"
      >
        <div className="flex items-center justify-between mb-2">
          <div 
            className="flex items-center overflow-hidden cursor-pointer flex-grow"
            onClick={() => navigate(`/band/${band._id}/lineup/${lineup._id}`)}
          >
            <UsersIcon className="h-5 w-5 mr-2 flex-shrink-0 text-muted-foreground" />
            <h3 className="text-lg font-medium truncate hover:text-blue-600">{lineup.name}</h3>
          </div>
          
          {/* Dropdown menu */}
          <div 
            className="relative" 
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowDropdown(!showDropdown);
              }}
              className="p-1 rounded-md hover:bg-background flex-shrink-0"
            >
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-border">
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    navigate(`/band/${band._id}/lineup/${lineup._id}`);
                  }}
                >
                  <UsersIcon className="h-4 w-4 mr-2" />
                  View Lineup
                </button>
                
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    handleEditLineup(lineup);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Lineup
                </button>
                
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    handleDuplicateLineup(lineup);
                  }}
                >
                  <CopyIcon className="h-4 w-4 mr-2" />
                  Duplicate Lineup
                </button>
                
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 text-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    handleDeleteLineup(lineup._id);
                  }}
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete Lineup
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Lineup Members Preview */}
        {lineup.members && lineup.members.length > 0 ? (
          <div className="mb-3">
            <div className="space-y-1">
              {lineup.members.slice(0, 4).map((member, index) => (
                <div key={index} className="flex items-center text-xs text-gray-600">
                  <span className="font-medium min-w-0 truncate flex-1">{member.name}</span>
                  <span className="text-gray-400 ml-2 flex-shrink-0">• {member.role}</span>
                </div>
              ))}
              {lineup.members.length > 4 && (
                <div className="text-xs text-gray-400 italic">
                  +{lineup.members.length - 4} more...
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-3 text-xs text-gray-400 italic">
            No members yet
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center">
            <CalendarIcon className="w-4 h-4 mr-1" />
            <span>{formatDate(lineup.created_at)}</span>
          </div>
          <div>
            {lineup.members?.length || 0} members
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Band Lineups</h2>
        {showNewLineupForm ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={lineupNameInput}
              onChange={(e) => setLineupNameInput(e.target.value)}
              placeholder="Lineup name..."
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAddLineup();
              }}
            />
            <button
              onClick={handleAddLineup}
              className="p-1 text-green-600 hover:text-green-800"
              title="Save"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setShowNewLineupForm(false);
                setLineupNameInput('');
              }}
              className="p-1 text-red-600 hover:text-red-800"
              title="Cancel"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowNewLineupForm(true)}
            className="flex items-center px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
            title="Add lineup"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            New Lineup
          </button>
        )}
      </div>

      {/* Lineup Cards */}
      {lineups.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
          <p className="text-muted-foreground">No lineups yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lineups.map(lineup => (
            <LineupCard key={lineup._id} lineup={lineup} />
          ))}
        </div>
      )}

      {/* Edit Lineup Modal */}
      {editingLineup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Lineup</h3>
              <button
                onClick={handleCloseEdit}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Lineup Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lineup Name
              </label>
              <input
                type="text"
                value={editingLineupName}
                onChange={(e) => handleUpdateLineupName(e.target.value)}
                placeholder="Enter lineup name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">Members</h4>
                <div className="flex items-center gap-2">
                  {hasChanges && (
                    <button
                      onClick={handleSave}
                      className="flex items-center px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm font-medium"
                      title="Save changes"
                    >
                      <CheckIcon className="w-3 h-3 mr-1" />
                      Save
                    </button>
                  )}
                  <button
                    onClick={handleAddMember}
                    className="flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
                    title="Add member"
                  >
                    <PlusIcon className="w-3 h-3 mr-1" />
                    Add Member
                  </button>
                </div>
              </div>

              {localMembers.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">No members in this lineup yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {localMembers.map((member, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={member.name}
                          onChange={(e) => handleUpdateMember(index, 'name', e.target.value)}
                          placeholder="Member name..."
                          className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <input
                          type="text"
                          value={member.role}
                          onChange={(e) => handleUpdateMember(index, 'role', e.target.value)}
                          placeholder="Role..."
                          className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => handleMoveMember(index, 'up')}
                          disabled={index === 0}
                          className="p-0.5 text-gray-600 hover:text-gray-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ChevronUpIcon className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleMoveMember(index, 'down')}
                          disabled={index === localMembers.length - 1}
                          className="p-0.5 text-gray-600 hover:text-gray-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ChevronDownIcon className="w-3 h-3" />
                        </button>
                      </div>

                      <button
                        onClick={() => handleDeleteMember(index)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                        title="Delete member"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={handleCloseEdit}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                Close
              </button>
              {hasChanges && (
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandLineupManager;